import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useNavigate } from "react-router-dom";
import ActivityIndicator from "../../components/ActivityIndicator";
import { WINDOW_HEIGHT } from "../../components/Util";
import ListItem, { TitleKey } from "../../components/reusable/ListItem";
import {
  useBrandStore,
  useNavigationStore,
  useVerificationStore,
} from "../../stores/storeContexts";
import { COLOR } from "../../styles/Colors";
import Search from "../../components/atoms/Search";
import { IBrandAccount } from "../../types/brandAccount";
import { formatDate } from "../../utils/formatDate";
import { BrandAccount } from "../../stores/brandStore";

const titles: Array<TitleKey> = [];

const keys = [
  { display: "No.", value: "number", size: 1 },
  {
    display: "Logo",
    value: "img",
    size: 2,
  },
  {
    display: "Brand Name",
    value: "name",
    size: 3,
  },
  {
    display: "Brand ID",
    value: "brandId",
    size: 3,
  },
  {
    display: "Category",
    value: "category",
    size: 4,
  },
  {
    display: "Total Campaign",
    value: "dealCount",
    size: 3,
  },
  {
    display: "Nego Campaign",
    value: "negoDealCount",
    size: 3,
  },
  {
    display: "Register At",
    value: "createdAt",
    size: 3,
  },
];

keys.forEach((key) => {
  titles.push({ display: key.display, size: key.size });
});

const BrandListScreen = () => {
  const { getBrandList, brandList, setSortBy, setSortOrder, setBrandListPage } =
    useVerificationStore();
  const { setBrand } = useBrandStore();
  const { pushStack } = useNavigationStore();
  const navigation = useNavigate();

  const [selectedList, setSelectedList] = useState<Array<string>>([]);
  const {
    sortBy = { display: "createdAt", value: "createdAt" },
    sortOrder,
    loading = false,
    data = [],
    page = 0,
  } = brandList;
  const [key, setKey] = useState<string>("createdAt");
  const [sort, swapSort] = useState<"asc" | "desc">("desc");
  const [keyword, setKeyword] = useState<string>("");
  const [date, setDate] = useState<Array<Date>>([]);

  const setSort = (key: string) => {
    if (sortBy.display !== key) {
      setSortBy(
        "brands",
        "list",
        keys[keys.findIndex((keyIndex) => keyIndex.display === key)]
      );
      setSortOrder("brands", "list", "asc");
    } else {
      if (sortOrder === "asc") {
        setSortOrder("brands", "list", "desc");
      } else if (sortOrder === "desc") {
        setSortOrder("brands", "list", "desc");
        setSortBy("brands", "list", {
          display: "createdAt",
          value: "createdAt",
        });
      }
    }
  };

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    if (layoutMeasurement.height + contentOffset.y >= contentSize.height) {
      setBrandListPage(page + 1);
    }
  };

  const searchData = (brand: { name: string }) => {
    if (!keyword) return true;
    const lowerCaseKeyword = keyword.toLowerCase();
    return !!brand.name && brand.name.toLowerCase().includes(lowerCaseKeyword);
  };

  const filterDate = (brand: IBrandAccount) => {
    const createdAt = new Date(brand.createdAt);
    switch (date.length) {
      case 0:
        return true;
      case 1:
        return (
          formatDate(createdAt.toString()) === formatDate(date[0].toString())
        );
      default:
        return !!createdAt && createdAt >= date[0] && createdAt <= date[1];
    }
  };

  const ListItemComponent = ({
    brand,
    index,
  }: {
    brand: BrandAccount;
    index: number;
  }) => {
    return (
      <ListItem
        onPress={() => {
          setBrand(brand);
          pushStack({
            display: brand?.name || "Brand",
            path: "/brand/deals",
          });
          navigation("/brand/deals");
        }}
        key={brand.brandId}
        keys={keys}
        ignored={["_id"]}
        data={brand}
        selectedList={selectedList}
        setSelectedList={setSelectedList}
        index={index}
      />
    );
  };

  const TableComponent = () => {
    const listItems: Array<JSX.Element> = [];
    data.map((brand, index) => {
      if (!!keyword) {
        if (searchData(brand)) {
          listItems.push(<ListItemComponent brand={brand} index={index} />);
        }
      } else if (!!date[0]) {
        if (filterDate(brand)) {
          listItems.push(<ListItemComponent brand={brand} index={index} />);
        }
      } else if (index < page * 10) {
        listItems.push(<ListItemComponent brand={brand} index={index} />);
      }
    });
    return <View>{listItems}</View>;
  };

  useEffect(() => {
    getBrandList(1);
  }, []);

  useEffect(() => {
    setSort(key);
  }, [key, sort]);

  if (loading) {
    return <ActivityIndicator style={{ marginTop: 32 }} />;
  }

  return (
    <View>
      <ListItem
        keys={keys}
        title={titles}
        selectedList={selectedList}
        setSelectedList={setSelectedList}
        sortOrder={sort}
        sortKey={sortBy.display}
        setSortKey={setKey}
        swapSort={() => {
          if (sort === "asc") {
            swapSort("desc");
          } else {
            swapSort("asc");
          }
        }}
      />
      <Search setKeyword={setKeyword} date={date} setDate={setDate} />
      <ScrollView
        onScroll={(event) => onScroll(event)}
        scrollEventThrottle={16}
        style={styles.scrollViewContainer}
      >
        <TableComponent />
      </ScrollView>
    </View>
  );
};

export default observer(BrandListScreen);

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: 1,
    borderColor: COLOR.LIGHTGRAY,
    borderStyle: "solid",
  },
  scrollViewContainer: {
    height: WINDOW_HEIGHT - 170,
  },
});
